import { exists } from '@jkhy/frontend-utils/functions';
import { hashString } from '@treasury/utils/functions';

/**
 * Generates a hashed ID based on specified keys of an object.
 * Useful for creating unique identifiers for objects that may not have a natural ID.
 * @param obj The object to generate the ID from.
 * @param keys The keys of the object to include in the hash.
 * @returns A numeric hash representing the unique ID.
 * @throws Error if any of the specified keys are missing or their values are undefined or empty.
 *
 * @example
 * const user = { firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com' };
 * const userId = createHashedId(user, 'firstName', 'lastName', 'email');
 */
export function createHashedId<T extends object>(obj: T, ...keys: (keyof T)[]): number {
    if (keys.some(key => !exists(obj[key]))) {
        throw new Error(
            'Could not create hashed ID. One or more specified keys are missing from the object.'
        );
    }

    const values = keys
        .sort((a, b) => a.toString().localeCompare(b.toString()))
        .map(key => JSON.stringify(obj[key]));
    return hashString(values.join('-'));
}
